import type { MinimalTarget } from '@/generated/model';

import { useDiagramSeriesOptions } from './useDiagramSeriesOptions';
import { useSeriesSelectionPreferences } from './useSeriesSelectionPreferences';
import { useTimeSeriesChartModel } from './useTimeSeriesChartModel';
import { useTimeSeriesChartState } from './useTimeSeriesChartState';

/**
 * Wires the diagram's state owners together, in data-flow order: the URL state picks the target and
 * window, the remembered selection picks the series, the series list validates that selection, and
 * the chart model turns it into what the chart draws. The component only lays the pieces out.
 */
export function useTimeSeriesChartController() {
  const state = useTimeSeriesChartState();
  const selection = useSeriesSelectionPreferences(state.targetId);

  const seriesOptions = useDiagramSeriesOptions({
    targetId: state.targetId,
    timeWindow: state.timeWindow,
    selectedSeriesIds: selection.selectedSeriesIds,
  });

  const chartModel = useTimeSeriesChartModel({
    targetId: state.targetId,
    timeWindow: state.timeWindow,
    requestedSeriesIds: selection.selectedSeriesIds,
    series: seriesOptions.series,
    selectedSeriesIds: seriesOptions.validSelectedSeriesIds,
    highlightedSeriesIds: selection.highlightedSeriesIds,
    getSeriesColor: seriesOptions.getSeriesColor,
  });

  function changeTarget(target: MinimalTarget | undefined) {
    if (target) {
      state.selectTarget(target);
      return;
    }

    state.clearTarget();
  }

  function changeDates(from: Date, to: Date) {
    state.changeDates({ from, to });
  }

  return {
    state,
    selection,
    seriesOptions,
    chartModel,
    // The chart always has navigation, so the hint shows whenever a chart is on screen.
    isNavigationHintVisible: chartModel.status.type === 'ready',
    changeTarget,
    changeDates,
  };
}
